'use client';

/**
 * Video Lab — funnel video intelligence.
 * Transcribe → AI proposes cuts/rewrites side-by-side → Boss edits/approves
 * → render (cuts + cloned-voice lip-sync) → ready for funnel A/B deploy.
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { FlaskConical, Loader2, Wand2, Scissors, Check, X, ArrowLeft, Upload } from 'lucide-react';

interface Seg { id: number; start: number; end: number; text: string }
interface Prop { id: number; action: 'keep' | 'cut' | 'replace'; newText?: string; reason?: string }
interface Voice { id: string; name: string; category: string }

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export default function VideoLabPage() {
  const [lab, setLab] = useState<string | null>(null);
  const [segments, setSegments] = useState<Seg[]>([]);
  const [props, setProps] = useState<Record<number, Prop>>({});
  const [voices, setVoices] = useState<Voice[]>([]);
  const [voiceId, setVoiceId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [render, setRender] = useState<any>(null);
  const [error, setError] = useState('');
  const [ret, setRet] = useState<any>(null);
  const [retHover, setRetHover] = useState<{ t: number; pct: number; x: number } | null>(null);
  const [retSeek, setRetSeek] = useState<number | null>(null); // clicked second → open player there
  const retVidRef = useRef<HTMLVideoElement>(null);
  const retChartRef = useRef<HTMLDivElement>(null);
  // safe-deploy state
  const [pf, setPf] = useState<any>(null);          // pre-flight report
  const [pfBusy, setPfBusy] = useState(false);
  const [dep, setDep] = useState<any>(null);        // deploy result
  const [depBusy, setDepBusy] = useState(false);
  const [pagePath, setPagePath] = useState('/start');

  const runPreflight = async () => {
    if (!lab) return;
    setPfBusy(true); setPf(null); setDep(null);
    const r = await fetch(`/api/funnels/videolab/deploy?funnel=olga-erm&lab=${lab}&file=output.mp4&pagePath=${encodeURIComponent(pagePath)}`).then(r => r.json()).catch(e => ({ error: String(e) }));
    setPf(r); setPfBusy(false);
  };

  const deploy = async () => {
    if (!lab || !pf?.ok) return;
    if (!confirm(`Deploy this variant LIVE to ${pagePath} as a 50/50 A/B test?\nControl keeps serving — rollback is one click.`)) return;
    setDepBusy(true);
    const r = await fetch('/api/funnels/videolab/deploy', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ funnel: 'olga-erm', lab, file: 'output.mp4', pagePath, testName: `beast-${lab}` }),
    }).then(r => r.json()).catch(e => ({ error: String(e) }));
    setDep(r); setDepBusy(false);
  };

  const rollback = async () => {
    if (!dep?.testId) return;
    const r = await fetch('/api/funnels/videolab/deploy', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ funnel: 'olga-erm', rollback: dep.testId }),
    }).then(r => r.json()).catch(e => ({ error: String(e) }));
    if (r.rolledBack) setDep({ ...dep, rolledBack: true });
    else alert(r.error || 'rollback failed');
  };
  const [labs, setLabs] = useState<any[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  // lip-sync spot-check: jump between replaced segments, toggle old vs new footage
  const vidRef = useRef<HTMLVideoElement>(null);
  const pendingSeek = useRef<number | null>(null);
  const [showOrig, setShowOrig] = useState(false);
  const [activeMk, setActiveMk] = useState<any>(null);

  const jumpTo = (m: any, orig: boolean) => {
    setActiveMk(m);
    const target = orig ? m.srcStart : m.start;
    if (orig !== showOrig) {
      pendingSeek.current = target;
      setShowOrig(orig);
    } else {
      const v = vidRef.current;
      if (v) { v.currentTime = target; v.play().catch(() => {}); }
    }
  };

  const loadLab = (labId: string) => {
    setBusy('Loading lab…'); setError('');
    fetch(`/api/funnels/videolab/transcribe?lab=${labId}`).then(r => r.json()).then(d => {
      setBusy(null);
      if (d.segments) {
        setLab(d.lab); setSegments(d.segments);
        if (d.proposals) {
          const m: Record<number, Prop> = {};
          for (const p of d.proposals) m[p.id] = p;
          setProps(m);
        }
      } else setError(d.error || 'lab not found');
    }).catch(() => setBusy(null));
    // pull existing render (player shows immediately if output exists)
    fetch(`/api/funnels/videolab/apply?job=${labId}`).then(r => r.json())
      .then(s => { if (s?.status) setRender(s); }).catch(() => {});
  };

  useEffect(() => {
    fetch('/api/content/revoice').then(r => r.json()).then(d => {
      setVoices(d.voices || []);
      if (d.voices?.length) setVoiceId(d.voices[0].id);
    }).catch(() => {});
    // load an existing lab from ?lab=<id>, otherwise list existing labs
    const labId = new URLSearchParams(window.location.search).get('lab');
    if (labId) loadLab(labId);
    else fetch('/api/funnels/videolab/transcribe').then(r => r.json()).then(d => setLabs(d.labs || [])).catch(() => {});
  }, []);

  // poll render
  useEffect(() => {
    if (!lab || render?.status !== 'running') return;
    const t = setInterval(async () => {
      const s = await fetch(`/api/funnels/videolab/apply?job=${lab}`).then(r => r.json()).catch(() => null);
      if (s) setRender(s);
      if (s?.status === 'done' || s?.status === 'failed') clearInterval(t);
    }, 4000);
    return () => clearInterval(t);
  }, [lab, render?.status]);

  useEffect(() => {
    fetch('/api/funnels/olga-erm/retention?videoId=main-vsl&bucketSeconds=1')
      .then(r => r.json()).then(d => { if (d?.retention) setRet(d); }).catch(() => {});
  }, []);

  const transcribe = async (f: File) => {
    setBusy('Transcribing (ElevenLabs STT)…'); setError(''); setSegments([]); setProps({}); setRender(null);
    const fd = new FormData();
    fd.append('video', f);
    const res = await fetch('/api/funnels/videolab/transcribe', { method: 'POST', body: fd }).then(r => r.json());
    setBusy(null);
    if (res.segments) { setLab(res.lab); setSegments(res.segments); }
    else setError(res.error || 'transcription failed');
  };

  const propose = async () => {
    setBusy('AI is analyzing the transcript…'); setError('');
    const res = await fetch('/api/funnels/videolab/propose', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ segments }),
    }).then(r => r.json());
    setBusy(null);
    if (res.proposals) {
      const m: Record<number, Prop> = {};
      for (const p of res.proposals) m[p.id] = p;
      setProps(m);
    } else setError(res.error || 'proposal failed');
  };

  const setAction = (id: number, action: Prop['action']) =>
    setProps(p => ({ ...p, [id]: { ...(p[id] || { id }), id, action } }));

  const approve = async () => {
    if (!lab) return;
    setError('');
    const plan = segments.map(s => ({
      id: s.id, start: s.start, end: s.end,
      action: props[s.id]?.action || 'keep',
      newText: props[s.id]?.action === 'replace' ? (props[s.id]?.newText || '') : undefined,
    }));
    const res = await fetch('/api/funnels/videolab/apply', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lab, plan, voiceId }),
    }).then(r => r.json());
    if (res.rendering) setRender({ status: 'running' });
    else setError(res.error || 'render failed to start');
  };

  const counts = segments.reduce((a, s) => {
    const act = props[s.id]?.action || 'keep';
    a[act] = (a[act] || 0) + 1; return a;
  }, {} as Record<string, number>);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500/25 to-violet-500/20 flex items-center justify-center"><FlaskConical className="w-5 h-5 text-cyan-300" /></span>
            Video Lab
          </h1>
          <p className="text-sm text-gray-400 mt-1">Transcript → AI proposal → your approval → recut + cloned voice → A/B on the funnel.</p>
        </div>
        <Link href="/funnels" className="text-sm text-gray-400 hover:text-cyan-300 flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Funnels</Link>
      </div>

      {/* Live retention curve — the dropOffCurve from the funnel platform */}
      {ret && (
        <div className="glass-card rounded-2xl p-5">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <h2 className="text-sm font-semibold text-white">Live VSL retention — {ret.videoId} · {Math.floor(ret.durationSeconds / 60)}m{ret.durationSeconds % 60}s · {ret.totalViews} views</h2>
            <span className="text-xs text-gray-500">source: yevlakhova.com /stats/videos/retention · per second</span>
          </div>
          {(() => {
            const c = ret.retention as { t: number; pct: number; viewers: number }[];
            const dur = ret.durationSeconds || c[c.length - 1]?.t || 1;
            const pts = c.map(p => `${(p.t / dur) * 100},${40 - (p.pct / 100) * 38}`).join(' ');
            const area = `0,40 ${pts} 100,40`;
            const timeAt = (clientX: number) => {
              const r = retChartRef.current!.getBoundingClientRect();
              const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
              const t = Math.round(frac * dur);
              const pt = c.reduce((a, b) => Math.abs(b.t - t) < Math.abs(a.t - t) ? b : a, c[0]);
              return { t, pct: pt.pct, x: frac * 100 };
            };
            return (
              <div ref={retChartRef} className="relative cursor-crosshair select-none"
                onMouseMove={e => setRetHover(timeAt(e.clientX))}
                onMouseLeave={() => setRetHover(null)}
                onClick={e => {
                  const { t } = timeAt(e.clientX);
                  setRetSeek(t);
                  const v = retVidRef.current;
                  if (v) { v.currentTime = t; v.play().catch(() => {}); }
                }}>
                <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-32 block">
                  {[25, 50, 75].map(g => (
                    <line key={g} x1="0" x2="100" y1={40 - (g / 100) * 38} y2={40 - (g / 100) * 38}
                      stroke="rgba(255,255,255,0.06)" strokeWidth="0.2" />
                  ))}
                  {/* minute grid */}
                  {Array.from({ length: Math.floor(dur / 60) }, (_, i) => (i + 1) * 60).map(t => (
                    <line key={t} x1={(t / dur) * 100} x2={(t / dur) * 100} y1="0" y2="40"
                      stroke="rgba(255,255,255,0.05)" strokeWidth="0.15" />
                  ))}
                  <polygon points={area} fill="url(#retGrad)" />
                  <polyline points={pts} fill="none" stroke="#22d3ee" strokeWidth="0.45"
                    strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                  {retHover && (
                    <line x1={retHover.x} x2={retHover.x} y1="0" y2="40" stroke="rgba(34,211,238,0.7)" strokeWidth="0.25" vectorEffect="non-scaling-stroke" />
                  )}
                  {retSeek != null && (
                    <line x1={(retSeek / dur) * 100} x2={(retSeek / dur) * 100} y1="0" y2="40" stroke="rgba(244,114,182,0.8)" strokeWidth="0.3" vectorEffect="non-scaling-stroke" />
                  )}
                  <defs>
                    <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(34,211,238,0.25)" />
                      <stop offset="100%" stopColor="rgba(34,211,238,0.02)" />
                    </linearGradient>
                  </defs>
                </svg>
                {retHover && (
                  <div className="absolute -top-1 px-2 py-1 rounded-md bg-black/85 border border-cyan-500/30 text-[11px] text-cyan-200 tabular-nums pointer-events-none whitespace-nowrap"
                    style={{ left: `${retHover.x}%`, transform: retHover.x > 80 ? 'translateX(-105%)' : 'translateX(8px)' }}>
                    {fmt(retHover.t)} · {retHover.pct.toFixed(1)}% watching · click to listen
                  </div>
                )}
              </div>
            );
          })()}
          {/* seconds axis — label every 2 minutes */}
          <div className="relative h-4 mt-0.5 text-[10px] text-gray-500 tabular-nums">
            {Array.from({ length: Math.floor(ret.durationSeconds / 120) + 1 }, (_, i) => i * 120).map(t => (
              <span key={t} className="absolute -translate-x-1/2" style={{ left: `${(t / ret.durationSeconds) * 100}%` }}>{fmt(t)}</span>
            ))}
            <span className="absolute right-0">{fmt(ret.durationSeconds)}</span>
          </div>
          {/* click-to-listen player: streams the original VSL from the lab */}
          {retSeek != null && (
            <div className="mt-3">
              <p className="text-[11px] text-gray-500 mb-1.5">Listening from <span className="text-pink-300 tabular-nums">{fmt(retSeek)}</span> — click anywhere on the curve to jump.</p>
              <video
                ref={retVidRef}
                src="/api/funnels/videolab/apply?file=olga-vsl&src=input"
                controls autoPlay
                onLoadedMetadata={() => { const v = retVidRef.current; if (v && retSeek != null) { v.currentTime = retSeek; v.play().catch(() => {}); } }}
                className="w-full max-w-2xl rounded-xl border border-white/[0.08]"
              />
            </div>
          )}
          {(() => {
            // cliffs: biggest audience losses over 10s windows (per-second data is noisy)
            const c = ret.retention; const drops: any[] = [];
            for (let i = 10; i < c.length; i += 1) drops.push({ t: c[i].t, d: c[i - 10].pct - c[i].pct, p: c[i].pct });
            const top = drops.sort((a, b) => b.d - a.d).slice(0, 3);
            return (
              <div className="flex gap-4 mt-3 flex-wrap">
                {top.map(x => (
                  <span key={x.t} className="text-xs px-2 py-1 rounded-lg bg-rose-500/10 text-rose-300 border border-rose-500/20">
                    cliff @ {Math.floor(x.t / 60)}m{String(x.t % 60).padStart(2, '0')}s — −{x.d.toFixed(1)}pp → {x.p.toFixed(1)}% left
                  </span>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Step 0: existing labs */}
      {!segments.length && labs.length > 0 && (
        <div className="glass-card rounded-2xl p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400 mb-3">Existing labs — click to open</div>
          <div className="flex flex-wrap gap-3">
            {labs.map(l => (
              <button key={l.lab} onClick={() => loadLab(l.lab)}
                className="rounded-xl px-4 py-3 text-left bg-white/[0.03] border border-white/[0.08] hover:border-cyan-500/40 hover:bg-cyan-500/[0.06] transition-all">
                <div className="text-sm font-medium text-white flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-cyan-400" /> {l.lab}
                </div>
                <div className="text-[11px] text-gray-400 mt-1">
                  {l.segments} segments{l.hasProposal ? ' · AI plan ready' : ''}{l.hasOutput ? ' · rendered' : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 1: upload */}
      {!segments.length && (
        <div className="glass-card rounded-2xl p-10 text-center">
          <input ref={fileRef} type="file" accept="video/*" className="hidden"
            onChange={e => e.target.files?.[0] && transcribe(e.target.files[0])} />
          {busy ? (
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" /><p className="text-sm">{busy}</p>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className="inline-flex flex-col items-center gap-3 text-gray-400 hover:text-cyan-300 transition-colors">
              <Upload className="w-10 h-10" />
              <span className="text-sm">Upload the funnel video (VSL) to transcribe</span>
            </button>
          )}
        </div>
      )}

      {/* Step 2: side-by-side transcript vs proposal */}
      {segments.length > 0 && (
        <>
          <div className="glass-card rounded-2xl p-4 flex items-center gap-3 flex-wrap">
            <button onClick={propose} disabled={!!busy}
              className="rounded-xl px-4 py-2 text-sm font-medium bg-gradient-to-r from-violet-500/20 to-cyan-500/15 text-violet-200 border border-violet-500/25 hover:from-violet-500/30 transition-all flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} AI proposal
            </button>
            <select value={voiceId} onChange={e => setVoiceId(e.target.value)}
              className="rounded-lg border border-white/[0.08] bg-[#0d1117]/85 px-3 py-2 text-sm text-white">
              {voices.map(v => <option key={v.id} value={v.id}>{v.category === 'cloned' ? '★ ' : ''}{v.name}</option>)}
            </select>
            <div className="ml-auto flex items-center gap-3 text-[11px] uppercase tracking-wider">
              <span className="text-gray-400">{counts.keep || 0} keep</span>
              <span className="text-red-400">{counts.cut || 0} cut</span>
              <span className="text-cyan-300">{counts.replace || 0} replace</span>
            </div>
            <button onClick={approve} disabled={render?.status === 'running' || !Object.keys(props).length}
              className="rounded-xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-cyan-500/25 to-blue-500/20 text-cyan-200 border border-cyan-500/30 hover:from-cyan-500/35 transition-all flex items-center gap-2 disabled:opacity-40">
              {render?.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Approve & render
            </button>
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-2 rounded-2xl order-last">
            {segments.map(s => {
              const p = props[s.id];
              const action = p?.action || 'keep';
              return (
                <div key={s.id} className={`glass-card rounded-xl p-4 grid lg:grid-cols-2 gap-4 ${action === 'cut' ? 'opacity-50' : ''}`}>
                  {/* original */}
                  <div>
                    <p className="text-[10px] text-gray-500 mb-1 tabular-nums">{fmt(s.start)} – {fmt(s.end)}</p>
                    <p className={`text-sm text-gray-300 ${action === 'cut' ? 'line-through' : ''}`}>{s.text}</p>
                  </div>
                  {/* proposal */}
                  <div className="flex gap-3">
                    <div className="flex flex-col gap-1">
                      {(['keep', 'cut', 'replace'] as const).map(a => (
                        <button key={a} onClick={() => setAction(s.id, a)}
                          className={`px-2 py-1 rounded-md text-[10px] uppercase tracking-wider border transition-colors ${
                            action === a
                              ? a === 'cut' ? 'bg-red-500/15 text-red-300 border-red-500/30'
                                : a === 'replace' ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30'
                                : 'bg-white/10 text-white border-white/20'
                              : 'border-white/[0.06] text-gray-500 hover:text-gray-300'}`}>
                          {a === 'cut' ? <Scissors className="w-3 h-3 inline" /> : null} {a}
                        </button>
                      ))}
                    </div>
                    <div className="flex-1">
                      {action === 'replace' ? (
                        <textarea value={p?.newText || ''} rows={3}
                          onChange={e => setProps(pr => ({ ...pr, [s.id]: { ...pr[s.id], id: s.id, action: 'replace', newText: e.target.value } }))}
                          placeholder="New text the cloned voice will speak…"
                          className="w-full rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] px-3 py-2 text-sm text-cyan-100 placeholder:text-gray-600 resize-y" />
                      ) : action === 'cut' ? (
                        <p className="text-xs text-red-400/80 italic pt-1">segment will be removed</p>
                      ) : (
                        <p className="text-xs text-gray-500 italic pt-1">unchanged</p>
                      )}
                      {p?.reason && <p className="text-[11px] text-gray-500 mt-1.5">AI: {p.reason}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Step 3: result */}
      {render && (
        <div className="glass-card rounded-2xl p-5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-3">Rendered result</p>
          {render.status === 'running' && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-400 text-sm"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /> Cutting + re-voicing… replaced segments run lip-sync (~1 min per 15s).</div>
              {render.progress?.total > 0 && (() => {
                const { done, total } = render.progress;
                const pct = Math.round((done / total) * 100);
                const elapsed = render.startedAt ? (Date.now() - render.startedAt) / 1000 : 0;
                const eta = done > 3 && elapsed > 0 ? Math.max(0, Math.round((elapsed / done) * (total - done))) : null;
                return (
                  <div>
                    <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between text-[11px] text-gray-500 mt-1.5">
                      <span>segment {done} / {total} · {pct}%</span>
                      {eta !== null && <span>~{eta >= 60 ? `${Math.floor(eta / 60)}m ${eta % 60}s` : `${eta}s`} left</span>}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {render.status === 'failed' && <pre className="text-xs text-red-400 whitespace-pre-wrap overflow-auto max-h-60">{render.log}</pre>}
          {render.status === 'done' && (
            <div className="grid lg:grid-cols-2 gap-5">
              <div>
                <video
                  ref={vidRef}
                  src={showOrig ? `${render.output}&src=input` : render.output}
                  controls
                  onLoadedMetadata={() => {
                    if (pendingSeek.current != null && vidRef.current) {
                      vidRef.current.currentTime = pendingSeek.current;
                      vidRef.current.play().catch(() => {});
                      pendingSeek.current = null;
                    }
                  }}
                  className={`w-full rounded-xl border ${showOrig ? 'border-yellow-500/40' : 'border-white/[0.08]'}`}
                />
                {render.markers?.replaced?.length > 0 && (() => {
                  const dur = render.markers.durationSeconds || 1;
                  return (
                    <div className="mt-3 space-y-2">
                      {/* timeline strip: cyan ticks = lip-synced moments */}
                      <div className="relative h-2 rounded-full bg-white/[0.06]">
                        {render.markers.replaced.map((m: any) => (
                          <button key={m.seg} onClick={() => jumpTo(m, false)}
                            title={`seg ${m.seg} · ${fmt(m.start)}–${fmt(m.end)} (${(m.end - m.start).toFixed(1)}s) — ${m.newText}`}
                            className="absolute top-0 h-full rounded-sm bg-cyan-400/80 hover:bg-cyan-300 transition-colors"
                            style={{ left: `${(m.start / dur) * 100}%`, width: `${Math.max(0.6, ((m.end - m.start) / dur) * 100)}%` }} />
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {render.markers.replaced.map((m: any) => (
                          <button key={m.seg} onClick={() => jumpTo(m, false)}
                            className={`text-[11px] px-2 py-1 rounded-lg border transition-colors ${activeMk?.seg === m.seg
                              ? 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40'
                              : 'bg-white/[0.03] text-gray-400 border-white/[0.08] hover:text-cyan-300'}`}>
                            ▶ {fmt(m.start)}–{fmt(m.end)} · {(m.end - m.start).toFixed(1)}s · seg {m.seg}
                          </button>
                        ))}
                        {activeMk && (
                          <button onClick={() => jumpTo(activeMk, !showOrig)}
                            className={`text-[11px] px-2 py-1 rounded-lg border font-medium transition-colors ${showOrig
                              ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40'
                              : 'bg-violet-500/15 text-violet-300 border-violet-500/30 hover:bg-violet-500/25'}`}>
                            {showOrig ? '← back to NEW (lip-sync)' : '⇄ show ORIGINAL at this moment'}
                          </button>
                        )}
                      </div>
                      {activeMk && <p className="text-[11px] text-gray-500 italic">“{activeMk.newText}”</p>}
                    </div>
                  );
                })()}
              </div>
              <div className="space-y-3 text-sm text-gray-300">
                <p className="font-medium text-white">Deploy as A/B variant — safely</p>
                <p className="text-gray-400 text-xs">Pre-flight verifies the video, the funnel API, and that no other test is running on the page. Nothing touches the live funnel until every check is green. Control keeps 50% and rollback is one click.</p>
                <div className="flex items-center gap-2">
                  <select value={pagePath} onChange={e => setPagePath(e.target.value)}
                    className="rounded-lg border border-white/[0.08] bg-[#0d1117]/85 px-3 py-2 text-sm text-white">
                    <option value="/start">/start (main VSL — 966 visits/mo)</option>
                    <option value="/thank-you">/thank-you</option>
                  </select>
                  <button onClick={runPreflight} disabled={pfBusy}
                    className="rounded-xl px-4 py-2 text-sm font-medium bg-white/[0.06] text-gray-200 border border-white/[0.1] hover:bg-white/[0.1] transition-all flex items-center gap-2 disabled:opacity-50">
                    {pfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Run pre-flight
                  </button>
                </div>
                {pf?.checks && (
                  <div className="space-y-1">
                    {pf.checks.map((c: any) => (
                      <div key={c.name} className="flex items-center gap-2 text-xs">
                        <span className={c.ok ? 'text-emerald-400' : 'text-rose-400'}>{c.ok ? '✓' : '✗'}</span>
                        <span className="text-gray-300">{c.name}</span>
                        <span className="text-gray-500">— {c.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
                {pf?.error && <p className="text-xs text-rose-400">{pf.error}</p>}
                {pf?.ok && !dep && (
                  <button onClick={deploy} disabled={depBusy}
                    className="rounded-xl px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-emerald-500/25 to-cyan-500/20 text-emerald-200 border border-emerald-500/30 hover:from-emerald-500/35 transition-all flex items-center gap-2 disabled:opacity-50">
                    {depBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Compressing + uploading + creating test…</> : <>🚀 Deploy 50/50 to {pagePath}</>}
                  </button>
                )}
                {dep?.deployed && !dep.rolledBack && (
                  <div className="space-y-2 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20 p-3">
                    <p className="text-emerald-300 text-sm font-medium">✓ LIVE — test “{dep.testName}” on {dep.pagePath} (id {dep.testId})</p>
                    <p className="text-xs text-gray-400">Watch per-variant retention in Funnels → A/B Tests. Control still serves {100 - (dep.weight || 50)}% of visitors.</p>
                    <button onClick={rollback} className="rounded-lg px-3 py-1.5 text-xs font-medium bg-rose-500/10 text-rose-300 border border-rose-500/30 hover:bg-rose-500/20 transition-colors">
                      ⏸ Rollback — pause test, 100% back to control
                    </button>
                  </div>
                )}
                {dep?.rolledBack && <p className="text-sm text-yellow-300">Test paused — funnel is 100% on the control video.</p>}
                {dep?.error && <p className="text-xs text-rose-400">{dep.error}</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
